import {createApp, watch, type Component} from "vue";
import { createPinia } from "pinia";
import piniaPluginPersistedstate from "pinia-plugin-persistedstate";

import vuetify from "@/plugins/vuetify";
import router from "@/router";
import translatorPlugin from "@/plugins/translatorPlugin";
import App from "./App.vue";
import validatorPlugin from "@/plugins/validatorPlugin";
import {
  faUtensils,faPaw,faChampagneGlasses,faBellConcierge,faCar,faBroom,faBreadSlice,faBicycle,
  faBed,faClock
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import {library} from "@fortawesome/fontawesome-svg-core";
import {useConsentStore} from "@/stores/consent";
import {initTelemetry} from "@/services/telemetry";
import {useSessionStore} from "@/stores/session";
import GlobalService from "@/services/global-service";

library.add(faUtensils,faPaw,faChampagneGlasses,faBellConcierge,faCar,faBroom,faBreadSlice,faBicycle,faBed,faClock);

const HOTJAR_ID = Number(process.env.VUE_APP_HOTJAR_ID);
const GOOGLE_ANALYTICS_ID = process.env.VUE_APP_GA_ID;
const SENTRY_DSN = process.env.VUE_APP_SENTRY_DSN;
const SENTRY_ENV = process.env.VUE_APP_SENTRY_ENV;

const app = createApp(App);
const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);

app
  .use(pinia)
  .use(vuetify)
  .use(router)
  .use(translatorPlugin)
  .use(validatorPlugin);

const fontAwesomeIcon = FontAwesomeIcon as unknown as Component;

app.component("font-awesome-icon", fontAwesomeIcon);
app.mount("#app");

const TRUTHY = new Set(["1", "true", "on", "yes", "y"]);
const isProd = process.env.NODE_ENV === "production";
const telemetryFlag = `${process.env.VUE_APP_ENABLE_TELEMETRY ?? ""}`.trim().toLowerCase();
const CLARITY_ID = process.env.VUE_APP_CLARITY_ID;

const session = useSessionStore();
const consent = useConsentStore();
const env = {
  isTelemetryEnabled: isProd && TRUTHY.has(telemetryFlag),
  GA_ID: GOOGLE_ANALYTICS_ID,
  HOTJAR_ID: Number.isFinite(HOTJAR_ID) ? HOTJAR_ID : undefined,
  SENTRY_DSN,
  SENTRY_ENV,
  CLARITY_ID
};

function tryInitTelemetry() {
  if (consent.allowAnalytics) {
    initTelemetry({ app, router, env });
  }
}

// If user already consented previously
tryInitTelemetry();

// React when the user makes a choice
consent.$subscribe((_mutation, state) => {
  if (state.decided && state.categories.analytics) {
    tryInitTelemetry();
  }
});

watch(
  () => session.language,
  (lang) => { GlobalService.translator().setLanguage(lang || "en"); },
  { immediate: true }
);

